import { useFrame } from '@react-three/fiber';
import { useAtomValue } from 'jotai';
import { useMemo, useRef } from 'react';
import * as THREE from 'three';

import {
  rainfallGullyTaggingAtom,
  rainfallPlaybackTimeAtom,
  rainfallStormScaleAtom,
  rainfallVisibleKindsAtom,
} from '@/atoms/rainfall.atom';
import type { LayerData } from '@/atoms';
import { computeMapOrigin } from '@/core';
import type { FeaturesQuery } from '@/graphql/graphql';
import { isPondDrained, playbackPondColour } from '@/lib/rainfallStyling';

type Feature = FeaturesQuery['featuresByLayer'][number];

interface Props {
  features: Feature[];
  layers: LayerData[] | null;
}

/**
 * 3D water-surface companion to the storm-playback bar. For each
 * pond polygon (in a RAINFALL_VECTOR layer that ran in STORM mode)
 * we build a flat horizontal plane at the current water level and
 * scale it from the pond's outer ring. The plane Z is read off the
 * playback time atom in a useFrame loop so we don't recompute the
 * geometry on every tick.
 *
 * Drained / gully-tagged ponds are skipped entirely — they never fill.
 */
export function RainfallPondFillAnimation3D({ features, layers }: Props) {
  const gullies = useAtomValue(rainfallGullyTaggingAtom);
  // Hide the water-fill animation when the user has hidden the pond
  // kind in the rainfall controls. Without this, the water surfaces
  // hover in mid-air with no pond polygon underneath — visually broken.
  const visibleKinds = useAtomValue(rainfallVisibleKindsAtom);
  const pondsVisible = visibleKinds.pond;

  const rainfallLayerIds = useMemo(() => {
    const ids = new Set<string>();
    for (const l of layers ?? []) {
      const meta = (l.metadata ?? {}) as { type?: string };
      if (meta.type === 'RAINFALL_VECTOR') ids.add(l.id);
    }
    return ids;
  }, [layers]);

  // Floating-origin shift to keep float32 precision tolerable. Same
  // mapOrigin scheme used by the rainfall arrows + GeoJson polygons.
  const mapOrigin = useMemo(
    () => (features?.length ? computeMapOrigin(features) : new THREE.Vector3(0, 0, 0)),
    [features],
  );

  // Build per-pond water-surface plane data once (geometry + Z range +
  // fillTimeS). Scoring per-frame work: we only flip the visibility +
  // adjust the Z, never rebuild the mesh.
  const planes = useMemo(() => {
    if (rainfallLayerIds.size === 0 || !features?.length) return [];
    const out: Array<{
      pondId: string;
      geometry: THREE.ShapeGeometry;
      lowZ: number;
      highZ: number;
      fillTimeS: number;
      drained: boolean;
    }> = [];
    for (const f of features) {
      if (!rainfallLayerIds.has(f.layerId as string)) continue;
      const props = (f.properties ?? {}) as Record<string, unknown>;
      if (props.kind !== 'pond') continue;
      const pondId = props.pondId as string | undefined;
      const fillTimeS = props.fillTimeS as number | undefined;
      const lowPointZ = props.lowPointZ as number | undefined;
      const overflowZ = props.overflowZ as number | undefined;
      if (
        !pondId ||
        typeof fillTimeS !== 'number' ||
        !Number.isFinite(fillTimeS) ||
        typeof lowPointZ !== 'number' ||
        typeof overflowZ !== 'number'
      ) {
        continue;
      }
      const geom = f.geometry as unknown as { type?: string; coordinates?: any } | null;
      const ring = pondOuterRing(geom);
      if (!ring || ring.length < 3) continue;

      // Build a 2D Shape in (x, y) — Three's Z-up is project convention,
      // so we compose the plane via ShapeGeometry on the XY plane and
      // translate by water-Z each frame.
      const shape = new THREE.Shape();
      const ox = ring[0][0] - mapOrigin.x;
      const oy = ring[0][1] - mapOrigin.y;
      shape.moveTo(ox, oy);
      for (let i = 1; i < ring.length; i++) {
        shape.lineTo(ring[i][0] - mapOrigin.x, ring[i][1] - mapOrigin.y);
      }
      const geometry = new THREE.ShapeGeometry(shape);
      out.push({
        pondId,
        geometry,
        lowZ: lowPointZ - mapOrigin.z,
        highZ: overflowZ - mapOrigin.z,
        fillTimeS,
        drained: isPondDrained(props, gullies),
      });
    }
    return out;
  }, [features, rainfallLayerIds, mapOrigin, gullies]);

  if (planes.length === 0 || !pondsVisible) return null;

  return (
    <group position={[mapOrigin.x, mapOrigin.y, mapOrigin.z]}>
      {planes.map((p) => (
        <PondWaterSurface
          key={p.pondId}
          geometry={p.geometry}
          lowZ={p.lowZ}
          highZ={p.highZ}
          fillTimeS={p.fillTimeS}
          drained={p.drained}
        />
      ))}
    </group>
  );
}

function PondWaterSurface({
  geometry,
  lowZ,
  highZ,
  fillTimeS,
  drained,
}: {
  geometry: THREE.ShapeGeometry;
  lowZ: number;
  highZ: number;
  fillTimeS: number;
  drained: boolean;
}) {
  const meshRef = useRef<THREE.Mesh>(null);
  const matRef = useRef<THREE.MeshBasicMaterial>(null);
  const currentT = useAtomValue(rainfallPlaybackTimeAtom);
  // What-if storm scale collapses the effective fill time by 1/scale —
  // a 2× design storm fills each pond in half the original time.
  // Read the atom outside useFrame so React re-renders us when the
  // slider moves; the per-frame body just consumes the latest value.
  const stormScale = useAtomValue(rainfallStormScaleAtom);

  useFrame(() => {
    const mesh = meshRef.current;
    const mat = matRef.current;
    if (!mesh || !mat) return;
    if (drained) {
      mesh.visible = false;
      return;
    }
    if (currentT <= 0) {
      // t=0: water at floor — invisible.
      mesh.visible = false;
      return;
    }
    mesh.visible = true;
    const safeScale = stormScale > 0 ? stormScale : 1;
    const effectiveFillTimeS = fillTimeS / safeScale;
    const fillFrac = Math.min(1, Math.max(0, currentT / effectiveFillTimeS));
    let waterZ: number;
    if (fillFrac >= 1) {
      waterZ = highZ;
    } else {
      waterZ = lowZ + fillFrac * (highZ - lowZ);
    }
    mesh.position.z = waterZ;
    // Mirror the 2D map's playback colour ramp (pale → mid → deep
    // blue) on the 3D water surface so both views agree on "how full"
    // at a glance. `playbackPondColour` is the same helper MapView's
    // pond fill paint uses.
    mat.color.set(playbackPondColour(fillFrac));
  });

  return (
    <mesh ref={meshRef} geometry={geometry} renderOrder={500} visible={false}>
      <meshBasicMaterial
        ref={matRef}
        color={'#3b82f6'}
        transparent
        opacity={0.6}
        depthTest={false}
        side={THREE.DoubleSide}
      />
    </mesh>
  );
}

function pondOuterRing(
  geom: { type?: string; coordinates?: any } | null | undefined,
): number[][] | null {
  if (!geom || !geom.coordinates) return null;
  if (geom.type === 'Polygon') return geom.coordinates[0] ?? null;
  if (geom.type === 'MultiPolygon') return geom.coordinates[0]?.[0] ?? null;
  return null;
}
